// eslint-disable-next-line object-curly-newline
import { getCurrentInstance, reactive, toRefs, watch } from 'vue'
import snackbar from '@/utils/snackbar'

// Thanks: https://medium.com/better-programming/reactive-vue-routes-with-the-composition-api-18c1abd878d1
export const useRouter = () => {
  const vm = getCurrentInstance().proxy

  const state = reactive({
    route: vm.$route,
  })

  watch(
    () => vm.$route,
    (r) => {
      state.route = r
    }
  )

  return { ...toRefs(state), router: vm.$router }
}

export function getUid() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    // eslint-disable-next-line no-bitwise
    const r = (Math.random() * 16) | 0
    // eslint-disable-next-line no-bitwise
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export function getToken() {
  const token = JSON.parse(sessionStorage.getItem('user')).buttonPermissions
    ? JSON.parse(sessionStorage.getItem('user')).token
    : ''

  return token
}

export function typeOf(obj) {
  return Object.prototype.toString.call(obj).slice(8, -1).toLowerCase()
}

/**
 * 手机号脱敏
 * @param {string} mobile
 * @returns {string}
 */
export const hideMobile = (mobile) =>
  mobile.replace(/^(\d{3})\d{4}(\d{4})$/, '$1****$2')

/**
 * 千分位格式化
 * @param {number} num
 * @returns {string}
 */
export function toThousands(num) {
  const str = num.toString()
  return str.replace(/(\d)(?=(?:\d{3})+$)/g, '$1,')
}

/**
 * 返回时间戳（默认当前时间）
 * 2015-03-05 17:59:00
 * @param {date} date
 * @returns {number}
 */
export const getTimeStamp = (date = new Date()) => date.getTime()

export const getTimeStampFromString = (dateAndTime) => {
  if (dateAndTime && typeOf(dateAndTime) === 'string') {
    const [date, time = []] = dateAndTime.split(' ') || []
    const dateInfo = date.split('-') || []
    let timeInfo
    if (time.length > 0) {
      timeInfo = time.split(':')
    } else {
      timeInfo = ['00', '00']
    }
    dateInfo.push(...timeInfo)
    if (dateInfo && dateInfo.length >= 5) {
      const [year, month, day, h, m] = dateInfo
      return getTimeStamp(new Date(year, month - 1, day, h, m))
    }
  }
  return undefined
}

/**
 * @description:根据'2022-11-16'格式的字符串 返回时间戳（默认当前时间）
 * @param {date} date 格式必须为 YYYY-MM-DD
 * @returns {number}
 */
export const getTimeStampByString = (dateAndTime) => {
  return getTimeStampFromString(dateAndTime) ?? getTimeStamp()
}

/**
 * @description: 根据时间戳格式化为目标字符串（默认当前时间）
 * @param {date} date
 * @param {string} format 格式化目标格式
 * @returns {number}
 */
export const formatDateByTimeStamp = (
  timeStamp = Date.now(),
  format = 'YYYY-MM-DD hh:mm:ss'
) => {
  const date = new Date(Number(timeStamp))
  const formatNumber = (n) => (n < 10 ? `0${n}` : n)
  const str = format
    .replace('YYYY', date.getFullYear())
    .replace('MM', formatNumber(date.getMonth() + 1))
    .replace('DD', formatNumber(date.getDate()))
    .replace('hh', formatNumber(date.getHours()))
    .replace('mm', formatNumber(date.getMinutes()))
    .replace('ss', formatNumber(date.getSeconds()))
  return str
}

/**
 * 根据数据字典value匹配label
 * @param {array} source
 * @param {number} id
 * @returns {string}
 */
export const getTextById = (source, id) => {
  const current = source.filter((item) => item.value === id)
  if (current.length) {
    return current[0].text
  }
  return ''
}

export const stringify = (obj) => {
  // 忽略undefined和function 先置为undefined 组合的时候忽略
  if (obj === undefined || typeof obj === 'function') {
    return undefined
  }

  // null或者NaN Infinity置为null
  if (obj === null) {
    return null
  }

  // 时间对象转为字符串
  if (obj instanceof Date) {
    return `"${obj.toJSON()}"`
  }

  // regExp输出{}
  if (obj instanceof RegExp) {
    return `{}`
  }

  // 字符串 置为“string” 布尔 数字原样输出
  if (typeof obj !== 'object') {
    return typeof obj === 'string' ? `"${obj}"` : obj
  }

  // 数组
  if (Array.isArray(obj)) {
    const arrStr = obj.map((item) => `${stringify(item)}`)
    return `[${arrStr.join(',')}]`
  }

  // 采用Object.getOwnPropertyNames直接过滤掉symbol
  const keyNames = Object.getOwnPropertyNames(obj).filter(
    (item) => item !== '__ob__'
  )
  const temp = []
  keyNames.forEach((item) => {
    temp.push(`${item}=${stringify(obj[item])}`)
  })
  const result = temp.join('&')
  return result
}

export const fileTypeEnum = [
  { type: 'image/jpg', number: 0 },
  { type: 'image/jpeg', number: 1 },
  { type: 'image/png', number: 2 },
  { type: 'image/gif', number: 3 },
  { type: 'video/mp4', number: 10 },
]
export const getMediaTypeNum = (fileType) => {
  let result = null
  fileTypeEnum.forEach((item) => {
    if (item.type === fileType) {
      result = item.number
    }
  })

  return result
}
/** 图片类型 */
export const imageType = [0, 1, 2, 3]
/** 视频类型 */
export const videoType = [10]

/** 允许上传图片类型 */
export const imgType = fileTypeEnum
  .filter((item) => item.type.startsWith('image'))
  .map((item) => item.type)

/** 允许上传视频类型 */
export const videoStrType = fileTypeEnum
  .filter((item) => item.type.startsWith('video'))
  .map((item) => item.type)

/**
 * @description: 防抖函数
 * @param {Function} func 函数
 * @param {number} delay 延迟执行毫秒数
 * @param {*} immediate 是否立即执行
 * @return {*}
 */
export const debounce = (func, delay = 200, immediate = false) => {
  let timer
  let _immediate = immediate
  return (...args) => {
    if (_immediate) {
      func.apply(this, args)
      _immediate = false
      return
    }
    clearTimeout(timer)
    timer = setTimeout(() => {
      func.apply(this, args)
    }, delay)
  }
}

/**
 * @description: 节流函数
 * @param {Function} func 函数
 * @param {number} wait 延迟执行毫秒数
 * @return {*}
 */
export const throttle = (func, wait) => {
  let previous = 0
  return (...args) => {
    const now = Date.now()
    if (now - previous > wait) {
      func.apply(this, args)
      previous = now
    }
  }
}

/**
 * @description: 返回时分秒格式
 * @param {number} s 秒
 * @return {string} 00:05:11
 */
export const formatSeconds = (s) =>
  new Date(s * 1000).toUTCString().match(/(\d\d:\d\d:\d\d)/)[0]

/**
 * @description: 匹配话题内容然后高亮
 * @return {string}
 */
export const formatTopicString = (str) => {
  const reg = /#.*?#/g
  return str.replace(reg, (value) => `<a>${value}</a>`)
}

/**
 * @description: 获取两数组差异项
 * @return {array} 差异项数组
 */
export const getArrDifference = (arr1, arr2) =>
  arr1.concat(arr2).filter((v, i, arr) => arr.indexOf(v) === arr.lastIndexOf(v))

/**
 * @description:字符串除首尾外隐藏
 * @return {string} A*****T
 */
export const getEncryptionCard = (str) => {
  const strArr = str.split('')
  let enStr = strArr[0]
  for (let i = 0; i < strArr.length - 2; i++) {
    enStr += '*'
  }
  enStr += strArr[strArr.length - 1]
  return enStr
}
/**
 * @description: 处理富文本里的图片宽度自适应
 * 1.去掉img标签里的style、width、height属性
 * 2.修改所有style里的width属性为max-width:100%
 * 3.去掉<br/>标签
 * 4.img标签添加style属性：max-width:100%;height:auto
 *
 * 具体需求待产品确认，现在只实现功能2
 * @param html
 * @returns {void|string|*}
 */
export const formatRichText = (html) => {
  // let newContent = html.replace(/<img[^>]*>/gi, (match) => {
  //   match = match.replace(/style="[^"]+"/gi, '').replace(/style='[^']+'/gi, '')
  //   match = match.replace(/width="[^"]+"/gi, '').replace(/width='[^']+'/gi, '')
  //   match = match
  //     .replace(/height="[^"]+"/gi, '')
  //     .replace(/height='[^']+'/gi, '')
  //   return match
  // })
  const newContent = html.replace(/style="[^"]+"/gi, (match) => {
    match = match
      .replace(/width:[^;]+;/gi, 'max-width:100%;')
      .replace(/width:[^;]+;/gi, 'max-width:100%;')
    return match
  })
  // newContent = newContent.replace(/<br[^>]*\/>/gi, '')
  // newContent = newContent.replace(
  //   /<img/gi,
  //   '<img style="max-width:100%;height:auto;display:block;margin-top:0;margin-bottom:0;"'
  // )
  return newContent
}

/**
 * @description: 获取cookie信息
 * @param {string} cookieName cookie名
 * @return {string}
 */
export const getCookie = (cookieName) => {
  if (document.cookie.length > 0) {
    let cookieStart = document.cookie.indexOf(`${cookieName}=`)
    if (cookieStart !== -1) {
      cookieStart = cookieStart + cookieName.length + 1
      let cookieEnd = document.cookie.indexOf(';', cookieStart)
      if (cookieEnd === -1) cookieEnd = document.cookie.length
      return unescape(document.cookie.substring(cookieStart, cookieEnd))
    }
  }
  return ''
}

/**
 * @description: 校验输入特殊字符
 * @param {string} str 输入字符
 * @return {string|boolean}
 */
export const special = (str) => {
  const pattern = /[`$%*]/im
  return !pattern.test(str) || '不能输入特殊字符'
}

/**
 *
 * @param {string} text 复制的文本
 */
export const copy = (text) => {
  const copyInput = document.createElement('input') // 创建input元素
  document.body.appendChild(copyInput) // 向页面底部追加输入框
  copyInput.setAttribute('value', text) // 添加属性，将text赋值给input元素的value属性
  copyInput.select() // 选择input元素
  document.execCommand('Copy') // 执行复制命令

  // 复制之后再删除元素，否则无法成功赋值
  copyInput.remove()
  snackbar.info('复制成功')
}

/**
 *
 * @param {string} text 需要脱敏的文本
 * @description: 脱敏处理，返回******，用于隐藏敏感信息
 * @returns
 */
export const maskField = (text) => {
  if (text) {
    return '******'
  }
  return ''
}
